import { useState } from "react";
import { searchPublicDebts } from "@/lib/publicPaymentsApi";
import { ContractInfo, PaymentDocument } from "../types";

interface SearchFormProps {
  onResults: (contract: ContractInfo, documents: PaymentDocument[]) => void;
  onClear: () => void;
}

function formatRut(value: string): string {
  const clean = value.replace(/[^0-9kK]/g, "").toUpperCase();
  if (clean.length <= 1) return clean;
  const body = clean.slice(0, -1);
  const dv = clean.slice(-1);
  const formatted = body.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${formatted}-${dv}`;
}

export default function SearchForm({ onResults, onClear }: SearchFormProps) {
  const [searchType, setSearchType] = useState<"rut" | "contrato">("rut");
  const [inputValue, setInputValue] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (searchType === "rut") {
      setInputValue(formatRut(val));
    } else {
      setInputValue(val.toUpperCase());
    }
    setError("");
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim()) {
      setError("Por favor ingresa un valor para buscar.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const payload = await searchPublicDebts(inputValue);
      onResults(payload.contract, payload.documents);
    } catch (err) {
      const fallback =
        searchType === "rut"
          ? "No se encontró ningún contrato asociado a ese RUT. Verifica el número e intenta nuevamente."
          : "No se encontró ningún contrato con ese número. Verifica e intenta nuevamente.";
      const message = err instanceof Error && err.message ? err.message : fallback;
      setError(message);
      onClear();
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setInputValue("");
    setError("");
    onClear();
  };

  return (
    <div className="bg-white rounded-2xl border border-sky-100 p-6 md:p-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-sky-100 text-sky-600">
          <i className="ri-search-line text-lg"></i>
        </div>
        <div>
          <h2 className="text-lg font-bold text-slate-800" style={{ fontFamily: "'Poppins', sans-serif" }}>
            Buscar documentos pendientes
          </h2>
          <p className="text-sm text-slate-500">Ingresa tu RUT o número de contrato</p>
        </div>
      </div>

      <div className="flex gap-2 mb-5 bg-slate-100 rounded-full p-1 w-fit">
        <button
          type="button"
          onClick={() => {
            setSearchType("rut");
            setInputValue("");
            setError("");
            onClear();
          }}
          className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
            searchType === "rut" ? "bg-white text-sky-600 shadow-sm" : "text-slate-500 hover:text-slate-700"
          }`}
        >
          <i className="ri-id-card-line mr-2"></i>Por RUT
        </button>
        <button
          type="button"
          onClick={() => {
            setSearchType("contrato");
            setInputValue("");
            setError("");
            onClear();
          }}
          className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
            searchType === "contrato" ? "bg-white text-sky-600 shadow-sm" : "text-slate-500 hover:text-slate-700"
          }`}
        >
          <i className="ri-file-list-3-line mr-2"></i>Por N° Contrato
        </button>
      </div>

      <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1 relative">
          <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-slate-400">
            <i className={searchType === "rut" ? "ri-user-line" : "ri-file-list-3-line"}></i>
          </div>
          <input
            type="text"
            value={inputValue}
            onChange={handleInput}
            placeholder={searchType === "rut" ? "Ej: 12.345.678-9" : "Ej: APR-001234"}
            className="w-full pl-11 pr-4 py-3 rounded-xl border border-slate-200 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent transition-all"
            maxLength={searchType === "rut" ? 12 : 20}
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-sky-500 text-white text-sm font-bold hover:bg-sky-600 transition-all duration-200 whitespace-nowrap cursor-pointer disabled:opacity-60"
        >
          {loading ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i>Buscando...
            </>
          ) : (
            <>
              <i className="ri-search-line"></i>Buscar
            </>
          )}
        </button>
        {inputValue && (
          <button
            type="button"
            onClick={handleClear}
            className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-slate-200 text-slate-500 text-sm font-medium hover:bg-slate-50 transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-close-line"></i>Limpiar
          </button>
        )}
      </form>

      {error && (
        <div className="mt-4 flex items-start gap-3 p-4 bg-red-50 border border-red-100 rounded-xl">
          <div className="w-5 h-5 flex items-center justify-center text-red-500 mt-0.5 shrink-0">
            <i className="ri-error-warning-line"></i>
          </div>
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <p className="mt-4 text-xs text-slate-400">
        <i className="ri-shield-check-line mr-1"></i>
        Tu información está protegida. Solo se muestran documentos asociados a tu contrato.
      </p>
    </div>
  );
}
